import * as Select from "@radix-ui/react-select";

import { IconChevronDown } from "./icons";

export interface ProfileOption {
  api_name: string;
  display_label: string;
}

interface ProfileSelectProps {
  slotLabel: "A" | "B";
  slotColor: string;
  value: string | null;
  onChange: (api_name: string) => void;
  options: ProfileOption[];
  exclude: string | null;
}

/**
 * Slot picker for Profile A / Profile B in the sidebar. Built on
 * @radix-ui/react-select so arrow keys, Enter, Escape, type-ahead and
 * screen reader support all come for free — the design bundle's plain
 * <div> menu with click-outside dismissal had none of that.
 *
 * Options that match `exclude` (the value held by the sibling slot) are
 * disabled. Picking a profile and then re-picking it in the other slot
 * is the only bug that this component structurally prevents.
 */
export function ProfileSelect({
  slotLabel,
  slotColor,
  value,
  onChange,
  options,
  exclude,
}: ProfileSelectProps) {
  const selected = options.find((p) => p.api_name === value) ?? null;

  return (
    <div style={{ position: "relative" }}>
      <div
        className="overline"
        style={{
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            background: slotColor,
            color: "white",
            display: "inline-grid",
            placeItems: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            fontWeight: 700,
          }}
        >
          {slotLabel}
        </span>
        Profile {slotLabel}
      </div>
      <Select.Root value={value ?? undefined} onValueChange={(v) => onChange(v)}>
        <Select.Trigger
          aria-label={`Profile ${slotLabel}`}
          style={{
            width: "100%",
            height: 34,
            padding: "0 10px",
            border: "1px solid var(--sidebar-border)",
            borderRadius: 8,
            background: "var(--background)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            color: selected ? "var(--foreground)" : "var(--muted-foreground)",
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              textAlign: "left",
            }}
          >
            <Select.Value placeholder="pick profile" />
          </span>
          <Select.Icon>
            <IconChevronDown size={13} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            sideOffset={4}
            style={{
              zIndex: 30,
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 10,
              boxShadow: "var(--shadow-dropdown)",
              padding: 4,
              maxHeight: 260,
              overflowY: "auto",
              minWidth: "var(--radix-select-trigger-width)",
            }}
          >
            <Select.Viewport>
              {options.length === 0 && (
                <div
                  style={{
                    padding: "10px 12px",
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                  }}
                >
                  No profiles for this mode.
                </div>
              )}
              {options.map((p) => {
                const disabled = p.api_name === exclude;
                const isSel = p.api_name === value;
                return (
                  <Select.Item
                    key={p.api_name}
                    value={p.api_name}
                    disabled={disabled}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "7px 10px",
                      borderRadius: 6,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.35 : 1,
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      background: isSel ? "var(--sidebar-accent)" : "transparent",
                      color: isSel ? "var(--atf-pink-700)" : "var(--foreground)",
                      fontWeight: isSel ? 600 : 500,
                      outline: "none",
                    }}
                  >
                    <Select.ItemText>{p.display_label}</Select.ItemText>
                    {disabled && (
                      <span
                        style={{
                          marginLeft: 6,
                          color: "var(--muted-foreground)",
                          fontSize: 10,
                        }}
                      >
                        (already Profile {slotLabel === "A" ? "B" : "A"})
                      </span>
                    )}
                  </Select.Item>
                );
              })}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
}
